import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import Editor from '@monaco-editor/react'
import { clsx } from 'clsx'
import { api, ApiError, type DatabaseRow, type QueryResult, type RiskReport, type TableRow } from '../lib/api'
import { formatBytes } from '../lib/format'
import { Spinner, Tag, ErrorNote, InfoNote, EmptyState } from '../components/ui/Primitives'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'

export function DatabaseTab({ deploymentId, caps }: { deploymentId: string; caps: Set<string> }) {
  const [selected, setSelected] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['databases', deploymentId],
    queryFn: () => api.get<DatabaseRow[]>('/databases'),
    select: (rows) => rows.filter((r) => r.deploymentId === deploymentId),
  })

  if (isLoading) return <Spinner className="h-6 w-6" />

  if (!data || data.length === 0) {
    return (
      <EmptyState
        title="Esta instancia no tiene bases registradas"
        hint="Podés registrar una base que ya exista (el panel no la toca, solo se conecta) o crear una nueva con usuario y contraseña generados."
      />
    )
  }

  const current = data.find((d) => d.id === selected) ?? data[0]!

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {data.map((d) => (
          <button
            key={d.id}
            onClick={() => setSelected(d.id)}
            className={clsx(
              'rounded-lg border px-3 py-1.5 text-sm transition-colors',
              current.id === d.id ? 'border-blue-500 bg-blue-500/10 text-blue-300' : 'hover:bg-slate-800/50',
            )}
          >
            {d.label}
            <span className="hint ml-2">{d.engine.toLowerCase()}</span>
          </button>
        ))}
      </div>

      {current.readOnlyMode && (
        <InfoNote>
          Esta base está marcada como <strong>solo lectura</strong> en el panel. Solo se aceptan
          consultas SELECT, aunque tengas permiso de escritura.
        </InfoNote>
      )}

      <SqlConsole database={current} caps={caps} />
    </div>
  )
}

function SqlConsole({ database, caps }: { database: DatabaseRow; caps: Set<string> }) {
  const [sql, setSql] = useState('SELECT 1')
  const [risk, setRisk] = useState<RiskReport | null>(null)
  const [result, setResult] = useState<QueryResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmName, setConfirmName] = useState<string | null>(null)

  const canWrite = caps.has('instance.db.write')

  const tables = useQuery({
    queryKey: ['db-tables', database.id],
    queryFn: () => api.get<TableRow[]>(`/databases/${database.id}/tables`),
  })

  const analyze = useMutation({
    mutationFn: () => api.post<RiskReport>(`/databases/${database.id}/explain-risk`, { sql }),
    onSuccess: (r) => setRisk(r),
  })

  const execute = useMutation({
    mutationFn: (v: { confirmName?: string }) =>
      api.post<QueryResult>(`/databases/${database.id}/query`, { sql, params: [], ...v }),
    onSuccess: (r) => {
      setResult(r)
      setError(null)
      setConfirmName(null)
    },
    onError: (err) => {
      setResult(null)
      if (err instanceof ApiError) {
        if (err.code === 'sql_needs_confirmation') {
          const detail = err.problem.detail as { reasons?: string[] } | undefined
          setRisk((prev) => prev ?? {
            readOnly: false, dangers: detail?.reasons ?? [], multiStatement: false,
            statements: [], requiresConfirmation: true,
            confirmName: database.deployment?.name ?? null, blockedReason: null,
          })
          setConfirmName(database.deployment?.name ?? null)
          return
        }
        const detail = err.problem.detail as { expectedName?: string } | undefined
        if (err.code === 'conflict' && detail?.expectedName) {
          setConfirmName(detail.expectedName)
          return
        }
      }
      setError(err instanceof Error ? err.message : 'La consulta falló')
    },
  })

  async function run() {
    setError(null)
    // Se consulta el riesgo ANTES de mandar: así el aviso aparece con el
    // detalle de por qué, en vez de un error seco después de ejecutar.
    const report = await api.post<RiskReport>(`/databases/${database.id}/explain-risk`, { sql }).catch(() => null)
    setRisk(report)

    if (report?.blockedReason) {
      setError(report.blockedReason)
      return
    }
    if (report?.requiresConfirmation && report.confirmName) {
      setConfirmName(report.confirmName)
      return
    }
    execute.mutate({})
  }

  return (
    <div className="grid gap-3 lg:grid-cols-[16rem_1fr]">
      <div className="card p-0">
        <p className="border-b px-4 py-2.5 text-sm font-medium">Tablas</p>
        <div className="max-h-64 divide-y divide-slate-800/60 overflow-auto">
          {tables.isLoading && <div className="p-4"><Spinner /></div>}
          {(tables.data ?? []).map((t) => (
            <button
              key={`${t.schema}.${t.name}`}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs hover:bg-slate-800/50"
              onClick={() => setSql(`SELECT * FROM ${t.name} LIMIT 100`)}
            >
              <span className="min-w-0 flex-1 truncate font-mono">{t.name}</span>
              {t.rowEstimate !== null && <span className="hint">{t.rowEstimate}</span>}
            </button>
          ))}
          {tables.data?.length === 0 && <p className="hint px-4 py-6">Sin tablas.</p>}
        </div>
      </div>

      <div className="min-w-0 space-y-3">
        <div className="card p-0">
          <Editor
            height="14rem"
            theme="vs-dark"
            language="sql"
            value={sql}
            onChange={(v) => { setSql(v ?? ''); setRisk(null) }}
            options={{ fontSize: 13, minimap: { enabled: false }, wordWrap: 'on', lineNumbers: 'off' }}
          />
          <div className="flex flex-wrap items-center gap-2 border-t px-4 py-2.5">
            <button className="btn-primary px-3 py-1.5 text-sm" onClick={() => void run()} disabled={execute.isPending}>
              {execute.isPending && <Spinner />} Ejecutar
            </button>
            <button className="btn-ghost px-3 py-1.5 text-sm" onClick={() => analyze.mutate()}>
              Analizar sin ejecutar
            </button>
            {!canWrite && <span className="hint">Solo lectura: te falta instance.db.write</span>}
            {result && (
              <span className="hint ml-auto">
                {result.rowCount} fila(s) · {result.durationMs} ms
                {result.truncated && ' · recortado'}
              </span>
            )}
          </div>
        </div>

        {risk && (risk.dangers.length > 0 || risk.blockedReason) && (
          <div className={clsx(
            'rounded-lg border px-4 py-3 text-sm',
            risk.blockedReason ? 'border-red-500/30 bg-red-500/10 text-red-300' : 'border-amber-500/30 bg-amber-500/10 text-amber-200',
          )}>
            <p className="font-medium">{risk.blockedReason ? 'Bloqueado' : 'Revisá esto antes de ejecutar'}</p>
            <ul className="mt-1.5 list-inside list-disc space-y-0.5">
              {risk.blockedReason ? <li>{risk.blockedReason}</li> : risk.dangers.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
            {risk.statements.some((s) => s.tables.length > 0) && (
              <p className="mt-2 text-xs opacity-80">
                Tablas afectadas: {[...new Set(risk.statements.flatMap((s) => s.tables))].join(', ')}
              </p>
            )}
          </div>
        )}

        {error && <ErrorNote>{error}</ErrorNote>}

        {result && (
          <div className="card overflow-auto p-0">
            {result.columns.length === 0 ? (
              <p className="px-5 py-4 text-sm">
                Listo. {result.affectedRows ?? 0} fila(s) afectada(s).
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    {result.columns.map((c) => (
                      <th key={c.name} className="whitespace-nowrap px-4 py-2.5 font-medium">
                        {c.name}
                        <span className="ml-1.5 font-normal normal-case opacity-60">{c.type}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {result.rows.map((row, i) => (
                    <tr key={i}>
                      {result.columns.map((c) => (
                        <td key={c.name} className="max-w-xs truncate px-4 py-2 font-mono text-xs">
                          {row[c.name] === null
                            ? <span className="text-slate-600 italic">null</span>
                            : String(row[c.name])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {result.truncated && (
              <p className="hint border-t px-4 py-2">
                Se muestran las primeras 1000 filas de {result.rowCount}. Agregá un LIMIT para ver menos.
              </p>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(confirmName)}
        title="Confirmar la consulta"
        description={
          risk?.dangers.length
            ? `Esta consulta ${risk.dangers.join('; ')}.`
            : 'Esta consulta necesita confirmación.'
        }
        confirmName={confirmName}
        confirmLabel="Ejecutar igual"
        busy={execute.isPending}
        onCancel={() => setConfirmName(null)}
        onConfirm={(typed) => execute.mutate({ confirmName: typed })}
      />
    </div>
  )
}

export { formatBytes }
